import { ApiGalleryItem, CanvasApiBaseResponse, VIDEO_ADDITIONS_URL } from '../../common/requestsTypings';
import { ResourceType, SharedData } from '../../common/typings';
import { GalleryItemHelper } from '../../GalleryItem/helpers';
import {
    FetchGalleryCategoriesResponse,
    FetchGalleryItemsResponse,
    GalleriesRequestData,
    GalleryCollectionItem,
    GalleryDataForPane,
    GalleryItemResponse,
    GalleryPaneTypes
} from '../../GalleryPane/typings';
import { CanvasApiMethodExecutor } from '../CanvasApiMethodExecutor';

const DEFAULT_GALLERY_ITEM = {
    ext: '',
    mimeType: '',
    name: '',
    size: 0
};

export class GalleryMethodExecutor extends CanvasApiMethodExecutor {
    async fetchGalleryFiles(
        type: ResourceType,
        paneType: GalleryPaneTypes,
        requestData: GalleriesRequestData,
        sharedData?: SharedData,
        isVideoAddition?: boolean,
        ideaId?: string
    ): Promise<GalleryDataForPane<GalleryCollectionItem>> {
        const { offset, limit, name, semantic_search, sort_order, groups, categories } = requestData;
        let path = '';

        // для картинок префикс video-additions добавляется если:
        // - это пэкшот со страницы создания видео-креатива
        // - это картинка со страницы создания аудио-креатива
        if (isVideoAddition) {
            path += VIDEO_ADDITIONS_URL;
        }

        path += '/files';

        switch (paneType) {
            case GalleryPaneTypes.recent:
                path += '/my';
                break;

            case GalleryPaneTypes.stock:
                path += '/stock';
                break;

            case GalleryPaneTypes.idea:
                path += `/idea/${ideaId}`;
                break;

            default:
                break;
        }

        let queryParams = [
            `type=${type}`,
            `offset=${offset}`,
            `limit=${limit}`,
            `name=${name}`,
            `semantic_search=${semantic_search}`,
            `sort_order=${sort_order}`
        ];

        if (categories && categories.length) {
            queryParams = queryParams.concat(categories.map(category => `category_id=${category}`));
        }

        if (groups && groups.length) {
            queryParams.push(`group_id=${groups.join(',')}`);
        }

        const response = await this.createRequest<FetchGalleryItemsResponse>(path, {}, sharedData, queryParams);

        return {
            collection: GalleryItemHelper.parseApiDataList(type, paneType, response.items),
            total: response.total
        };
    }

    async fetchGalleryCategories(type: ResourceType, sharedData?: SharedData): Promise<FetchGalleryCategoriesResponse> {
        const path = type === ResourceType.image ? 'stock/categories' : `${VIDEO_ADDITIONS_URL}/stock/groups`;

        return await this.createRequest<FetchGalleryCategoriesResponse>(path, {}, sharedData);
    }

    async editGalleryItem(
        itemId: string,
        changedProps: Partial<ApiGalleryItem>,
        sharedData: SharedData,
        isVideoAddition?: boolean
    ): Promise<GalleryItemResponse> {
        const path = `${isVideoAddition ? VIDEO_ADDITIONS_URL : ''}files/${itemId}`;
        const params = {
            method: 'PUT',
            body: JSON.stringify(changedProps)
        };

        return await this.createRequest<GalleryItemResponse>(path, params, sharedData);
    }

    async applyExternalGalleryItem(
        resourceType: ResourceType,
        galleryType: GalleryPaneTypes,
        resourceId: string,
        ideaId?: string,
        isVideoAddition?: boolean,
        sharedData?: SharedData
    ): Promise<GalleryItemResponse> {
        const galleryItem = {
            ...DEFAULT_GALLERY_ITEM,
            resourceType,
            galleryType,
            resourceId,
            options: {
                idea: {
                    ideaId
                }
            }
        };

        const path = [];

        if (isVideoAddition) {
            path.push(VIDEO_ADDITIONS_URL);
        }

        if (galleryType === GalleryPaneTypes.stock) {
            path.push('files/from-stock', resourceId, 'used');
        } else if (galleryType === GalleryPaneTypes.idea) {
            path.push('files/idea', ideaId, 'file', resourceId);
        }

        const params = {
            method: 'POST',
            body: JSON.stringify(galleryItem)
        };

        return await this.createRequest<GalleryItemResponse>(path.join('/'), params, sharedData);
    }

    async removeGalleryItem(
        id: string,
        isVideoAddition?: boolean,
        sharedData?: SharedData
    ): Promise<CanvasApiBaseResponse> {
        const path = `${isVideoAddition ? VIDEO_ADDITIONS_URL : ''}/files/${id}`;
        const params = {
            method: 'DELETE'
        };
        return await this.createRequest<CanvasApiBaseResponse>(path, params, sharedData);
    }
}
